import { Node, Descendant, Element as SlateElement, Text } from "slate";

import { RichTextDocument } from "../RichText";

// Simple serialization: Slate to Markdown
export const serializeRichTextDocumentToMarkdown = (nodes: RichTextDocument): string => {
    try {
        // Vérifier que nodes est valide avant de traiter
        if (!nodes || !Array.isArray(nodes)) {
            return "";
        }
        return nodes.map(node => serializeNode(node)).join("\n");
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erreur de sérialisation:", error);
        return "";
    }
};

const serializeNode = (node: Node): string => {
    if (Text.isText(node)) {
        let text = node.text;
        if (node.bold) {
            text = `**${text}**`;
        }
        if (node.italic) {
            text = `_${text}_`;
        }
        if (node.underline) {
            text = `<u>${text}</u>`;
        }
        if (node["strike-through"]) {
            text = `~~${text}~~`;
        }
        if (node.code) {
            text = `\`${text}\``;
        }
        if (node.link) {
            text = `[${text}](${node.link})`;
        }
        return text;
    }

    if (SlateElement.isElement(node)) {
        // Special handling for code blocks
        if (node.type === "code") {
            const codeContent = node.children
                .map(child => {
                    if (Text.isText(child)) {
                        return child.text;
                    }
                    return serializeNode(child);
                })
                .join("");

            // Add language if available
            const language = (node as any).language || "";
            return `\`\`\`${language}\n${codeContent}\n\`\`\``;
        }

        const children = node.children.map(child => serializeNode(child)).join("");

        switch (node.type) {
            case "heading-one":
                return `# ${children}`;
            case "heading-two":
                return `## ${children}`;
            case "heading-three":
                return `### ${children}`;
            case "block-quote":
                return `> ${children}`;
            case "citation":
                return `> ${children}`;
            case "image":
                return `![Image](${node.url})`;
            case "bulleted-list":
                return node.children.map(child => `- ${serializeNode(child)}`).join("\n");
            case "numbered-list":
                return node.children.map((child, index) => `${index + 1}. ${serializeNode(child)}`).join("\n");
            case "list-item":
                return children;
            default:
                return children;
        }
    }

    return "";
};

const hasText = (value: RichTextDocument): boolean => {
    return value.some(node => {
        if (Text.isText(node)) {
            return node.text.trim().length > 0;
        }
        if (SlateElement.isElement(node)) {
            if (node.type === "image") {
                return Boolean(node.url);
            }
            return hasText(node.children);
        }

        return false;
    });
};

export const isRichTextDocumentEmpty = (value: RichTextDocument) => !hasText(value);

// Simple deserialization: Markdown to Slate
export const deserializeMarkdownToRichTextDocument = (markdownText: string): RichTextDocument => {
    try {
        if (!markdownText?.trim()) {
            return [{ type: "paragraph", children: [{ text: "" }] }];
        }

        const lines = markdownText.replace(/\u00A0/g, " ").split(/[\n\r]/);
        const result: Descendant[] = [];
        let currentList: any = null;
        let currentListType: string | null = null;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Handle bulleted lists (with * or -)
            if (line.match(/^\s*[-*]\s+(.*)/)) {
                const content = line.replace(/^\s*[-*]\s+/, "");
                const listItem = {
                    type: "list-item",
                    children: parseInlineFormatting(content),
                };

                if (currentListType !== "bulleted-list") {
                    // Close the previous list if necessary
                    if (currentList) {
                        result.push(currentList);
                    }
                    // Start a new list
                    currentList = {
                        type: "bulleted-list",
                        children: [listItem],
                    };
                    currentListType = "bulleted-list";
                } else {
                    currentList.children.push(listItem);
                }
                continue;
            }

            // Handle numbered lists
            if (line.match(/^\s*\d+\.\s+(.*)/)) {
                const content = line.replace(/^\s*\d+\.\s+/, "");
                const listItem = {
                    type: "list-item",
                    children: parseInlineFormatting(content),
                };

                if (currentListType !== "numbered-list") {
                    // Close the previous list if necessary
                    if (currentList) {
                        result.push(currentList);
                    }
                    // Start a new list
                    currentList = {
                        type: "numbered-list",
                        children: [listItem],
                    };
                    currentListType = "numbered-list";
                } else {
                    currentList.children.push(listItem);
                }
                continue;
            }

            // Skip empty lines when in a list
            if (currentList && line.trim() === "") {
                continue;
            }

            // If we're no longer in a list, close the current list
            if (currentList) {
                result.push(currentList);
                currentList = null;
                currentListType = null;
            }

            // Handle headings (normalize spaces to handle non-breaking spaces)
            const normalizedLine = line.replace(/\u00A0/g, " "); // Replace non-breaking spaces with normal spaces

            if (normalizedLine.startsWith("### ")) {
                result.push({
                    type: "heading-three",
                    children: parseInlineFormatting(normalizedLine.substring(4)),
                });
            } else if (normalizedLine.startsWith("## ")) {
                result.push({
                    type: "heading-two",
                    children: parseInlineFormatting(normalizedLine.substring(3)),
                });
            } else if (normalizedLine.startsWith("# ")) {
                result.push({
                    type: "heading-one",
                    children: parseInlineFormatting(normalizedLine.substring(2)),
                });
            }
            // Handle quotes - support spaces before and after the > for legacy DraftJS compatibility
            else if (normalizedLine.match(/^(\s*)>\s*(.*)$/)) {
                const quoteMatch = normalizedLine.match(/^(\s*)>\s*(.*)$/);
                const [, , content] = quoteMatch!;
                result.push({
                    type: "citation",
                    children: parseInlineFormatting(content),
                });
            }
            // Handle code blocks
            else if (line.startsWith("```")) {
                const codeContent: string[] = [];
                // Extract language if present
                const language = line.substring(3).trim() || undefined;

                i++; // Skip the opening line
                while (i < lines.length && !lines[i].startsWith("```")) {
                    codeContent.push(lines[i]);
                    i++;
                }

                const codeElement: any = {
                    type: "code",
                    children: [{ text: codeContent.join("\n") }],
                };

                // Add language if present
                if (language) {
                    codeElement.language = language;
                }

                result.push(codeElement);
            }
            // Handle normal paragraphs
            else if (line.trim()) {
                // Check if it's a Markdown image
                const imageMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
                if (imageMatch) {
                    const [, , url] = imageMatch;
                    result.push({
                        type: "image",
                        url,
                        children: [{ text: "" }],
                    });
                } else {
                    // Treat all other cases as paragraphs with inline formatting
                    result.push({
                        type: "paragraph",
                        children: parseInlineFormatting(line),
                    });
                }
            }
        }

        // Close the last list if necessary
        if (currentList) {
            result.push(currentList);
        }

        return result.length > 0 ? result : [{ type: "paragraph", children: [{ text: "" }] }];
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Erreur de désérialisation:", error);
        return [{ type: "paragraph", children: [{ text: markdownText || "" }] }];
    }
};

// Function to parse inline formatting (bold, italic, etc.)
const parseInlineFormatting = (text: string): any[] => {
    if (!text) {
        return [{ text: "" }];
    }

    const result: any[] = [];
    let currentText = "";
    let i = 0;

    // Simple helper function to handle single delimiter formatting
    const handleFormat = (delimiter: string, property: string, value: any = true): boolean => {
        if (text.substring(i, i + delimiter.length) === delimiter) {
            if (currentText) {
                result.push({ text: currentText });
                currentText = "";
            }
            i += delimiter.length;
            let formattedText = "";
            while (i < text.length - delimiter.length + 1 && text.substring(i, i + delimiter.length) !== delimiter) {
                formattedText += text[i];
                i++;
            }
            if (text.substring(i, i + delimiter.length) === delimiter) {
                result.push({ text: formattedText, [property]: value });
                i += delimiter.length;
            } else {
                currentText += delimiter + formattedText;
            }
            return true;
        }
        return false;
    };

    while (i < text.length) {
        // Bold (**text** or __text__) - multi-character first
        if (handleFormat("**", "bold") || handleFormat("__", "bold")) {
            continue;
        }
        // Strikethrough (~~text~~) - multi-character
        else if (handleFormat("~~", "strike-through")) {
            continue;
        }
        // Italic (*text*) - single character, won't match ** because already handled
        else if (handleFormat("*", "italic")) {
            continue;
        }
        // Italic (_text_) - single character, won't match __ because already handled
        else if (handleFormat("_", "italic")) {
            continue;
        }
        // Code inline (`text`) - single character
        else if (handleFormat("`", "code")) {
            continue;
        }
        // Underlined (<u>text</u>)
        else if (text.substring(i, i + 3) === "<u>") {
            if (currentText) {
                result.push({ text: currentText });
                currentText = "";
            }
            i += 3;
            let underlineText = "";
            while (i < text.length - 3 && text.substring(i, i + 4) !== "</u>") {
                underlineText += text[i];
                i++;
            }
            if (text.substring(i, i + 4) === "</u>") {
                result.push({ text: underlineText, underline: true });
                i += 4;
            } else {
                currentText += "<u>" + underlineText;
            }
        }
        // Inline links [text](url)
        else if (text[i] === "[") {
            if (currentText) {
                result.push({ text: currentText });
                currentText = "";
            }
            i += 1;
            let linkText = "";
            while (i < text.length && text[i] !== "]") {
                linkText += text[i];
                i++;
            }
            if (text[i] === "]" && text[i + 1] === "(") {
                i += 2; // Skip "]("
                let linkUrl = "";
                while (i < text.length && text[i] !== ")") {
                    linkUrl += text[i];
                    i++;
                }
                if (text[i] === ")") {
                    // Treat the link as a mark instead of an element
                    result.push({ text: linkText, link: linkUrl });
                    i += 1;
                } else {
                    currentText += "[" + linkText + "](" + linkUrl;
                }
            } else {
                currentText += "[" + linkText;
            }
        }
        // Normal character
        else {
            currentText += text[i];
            i++;
        }
    }

    // Add remaining text
    if (currentText) {
        result.push({ text: currentText });
    }

    return result.length > 0 ? result : [{ text: "" }];
};

// Utility function to validate Slate structure
export const validateSlateStructure = (nodes: Descendant[]): Descendant[] => {
    return nodes.map(node => {
        if (node && typeof node === "object" && "type" in node && "children" in node) {
            // Ensure each element has children
            const element = node as any;
            if (!element.children || element.children.length === 0) {
                return { ...element, children: [{ text: "" }] };
            }
            // Recursively validate children
            return { ...element, children: validateSlateStructure(element.children) };
        }
        // For text nodes, ensure they have a text property
        if (node && typeof node === "object" && "text" in node) {
            const textNode = node as any;
            return { ...textNode, text: textNode.text || "" };
        }
        return node;
    });
};
